import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Auth0Provider, AppState, useAuth0 } from '@auth0/auth0-react';

const queryClient = new QueryClient();

export const endpointLink = "https://odp.golf/api/";
export const websocketLink = "wss://odp.golf/websocket";

// FPM (Facility Power Management) backend.
// Power/charger pages in GolfTrack proxy to FPM's API so there's a single
// source of truth. Override at build time via REACT_APP_FPM_API_URL /
// REACT_APP_FPM_WS_URL when running against a non-default FPM host.
export const fpmEndpointLink =
  process.env.REACT_APP_FPM_API_URL || "https://fpm.odp.golf/api/";
export const fpmWebsocketLink =
  process.env.REACT_APP_FPM_WS_URL || "wss://fpm.odp.golf/websocket";

const auth0Domain = process.env.AUTHO_DOMAIN;
const auth0ClientId = process.env.AUTHO_CLIENT_ID;
const auth0Audience = process.env.AUTHO_AUDIENCE;
const auth0Redirect = process.env.AUTHO_REDIRECT_URI || "https://odp.golf/"

// After Auth0 finishes the redirect dance, put the URL back to whatever the
// user was originally trying to reach. We use history.replaceState (not
// react-router's useNavigate) because Auth0Provider sits OUTSIDE <Router/>
// and this callback fires while the app is still in its loading state, before
// <Routes/> has read window.location.
const onRedirectCallback = (appState?: AppState) => {
  const target = appState?.returnTo || window.location.pathname || '/';
  window.history.replaceState({}, document.title, target);
};

// Loading component
const LoadingScreen = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'black',
    zIndex: 9999
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ 
        width: '50px', 
        height: '50px', 
        border: '5px solid #f3f3f3',
        borderTop: '5px solid yellow',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto'
      }} />
      <p style={{ marginTop: '20px' }}>Loading...</p>
    </div>
  </div>
);

// Auth wrapper component
const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isLoading, error } = useAuth0();

  if (error) {
    return <div>Authentication Error: {error.message}</div>;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <Auth0Provider
        domain={auth0Domain || "dev-u0u6agcpjzmqzm01.us.auth0.com"}
        clientId={auth0ClientId || "KvNtMjfo3ZgxNwIJuO0WnPBPdOKEjOgG"}
        authorizationParams={{ 
          audience: auth0Audience || "https://odp.golf/audience",
          redirect_uri: window.location.origin,
          scope: "openid profile email offline_access"
        }}
        onRedirectCallback={onRedirectCallback}
        cacheLocation="localstorage"
        useRefreshTokens={true}
      >
        <QueryClientProvider client={queryClient}>
          <AuthWrapper>
            <App />
          </AuthWrapper>
        </QueryClientProvider>
      </Auth0Provider>
    </React.StrictMode>
  );
} else {
  console.error("Root element not found");
}

// Add the spin animation to your CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

